import { Component } from '@theme/component';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * Horizontal row of campaign photos with previous/next buttons that scroll the native track.
 *
 * @typedef {object} CampaignRowRefs
 * @property {HTMLElement} track - The scrolling list of photos
 * @property {HTMLButtonElement} [previous] - Scrolls towards the start
 * @property {HTMLButtonElement} [next] - Scrolls towards the end
 *
 * @extends {Component<CampaignRowRefs>}
 */
class LaauCampaignRow extends Component {
  requiredRefs = ['track'];

  /** @type {AbortController | null} */
  #controller = null;

  #frame = 0;

  connectedCallback() {
    super.connectedCallback();
    this.#listen();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#listen();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
  }

  /**
   * Scrolls one view towards the start of the row.
   * @param {MouseEvent} [_event]
   */
  scrollPrevious(_event) {
    this.#step(-1);
  }

  /**
   * Scrolls one view towards the end of the row.
   * @param {MouseEvent} [_event]
   */
  scrollNext(_event) {
    this.#step(1);
  }

  #listen() {
    this.#controller?.abort();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { track } = this.refs;

    track.addEventListener('scroll', () => this.#queueSync(), { passive: true, signal });
    window.addEventListener('resize', () => this.#queueSync(), { signal });
    this.#sync();
  }

  /** @param {number} direction */
  #step(direction) {
    const { track } = this.refs;
    const rtl = getComputedStyle(track).direction === 'rtl' ? -1 : 1;
    track.scrollBy({
      left: track.clientWidth * 0.8 * direction * rtl,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  #queueSync() {
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => this.#sync());
  }

  #sync() {
    const { track, previous, next } = this.refs;
    const max = track.scrollWidth - track.clientWidth;
    const position = Math.abs(track.scrollLeft);

    this.toggleAttribute('data-scrollable', max > 1);
    if (previous) previous.disabled = position <= 1;
    if (next) next.disabled = position >= max - 1;
  }
}

if (!customElements.get('laau-campaign-row')) {
  customElements.define('laau-campaign-row', LaauCampaignRow);
}
